/**
 * Fetch Configuration
 *
 * Fetches configuration from CDN and applies translations for legacy rule formats.
 * Based on V1's loadConfiguration.js
 */

import translateRules from '@/configuration/translateRules';
import translateToDisplayRules from '@/configuration/translateToDisplayRules';
import type { ServerConfiguration } from '@/domain/configuration';
import { validServerConfiguration } from '@/domain/validators/validServerConfiguration';
import { logger } from '@/lib/logger';
import sofetch from '@/lib/sofetch';

export default async function fetchConfiguration(cdn: string, appId: string): Promise<ServerConfiguration> {
  let response;
  try {
    logger.log('Fetching configuration', { cdn, appId });
    const url = `${cdn}/orgs/configs/chat/${appId}.json?q=${new Date().getTime()}`;
    response = await sofetch.get(url, { cache: 'no-cache' });
  } catch (error) {
    const status = error instanceof Response ? error.status : 'unknown';
    throw new Error(`Failed to fetch configuration for appID: ${appId}, (${status})`);
  }

  if (!response.data) {
    throw new Error(`Failed to fetch configuration for appID: ${appId}, empty configuration`);
  }

  // Validate the configuration data structure
  const config = validServerConfiguration(response.data);

  // Translate old rules model to new model (until migration of old model happens)
  if (config.rules) {
    const translatedRules = translateRules(config.rules);
    config.rules = translatedRules;
  }

  // Generate displayRules if not present
  if (!config.displayRules && config.display) {
    // translateToDisplayRules accepts Record<string, LegacyRule> | null | undefined
    // Our rules are Record<string, unknown> which is compatible with LegacyRule
    // since LegacyRule has [key: string]: unknown
    config.displayRules = translateToDisplayRules(config.display, config.rules || null);
  }

  logger.log('Server configuration fetched', {
    provider: config.provider,
    orgId: config.orgId,
  });

  return config;
}
